import { useMemo } from 'react';
import { usePermissions } from '@/features/auth';
import { useVehicleMileage } from '@/features/vehiclesearch/hooks/useVehicleMileage';
import { useDealersByIds, buildDealerNameMap } from '@features/dealers/hooks/useDealersByIds';
import { useAgreementSoldItemsQuery } from './useAgreementSoldItemsQuery';
import { useAgreementInvoicesQuery } from './useAgreementInvoicesQuery';
import { useCashPositionWatch } from './useCashPositionWatch';
import { useInvoicingCandidatesQuery } from './useInvoicingCandidatesQuery';
import { useReconciledSoldLines } from './useReconciledSoldLines';
import { useInvoiceSelection } from './useInvoiceSelection';
import { useInvoiceSubmission } from './useInvoiceSubmission';
import { useInvoiceReversal } from './useInvoiceReversal';
import type { InvoiceReversal } from './useInvoiceReversal';
import { toInvoiceLines } from '../utils/toInvoiceLines';
import { toScaled } from '../utils/invoiceSelection';
import { useFundingApproval } from './useFundingApproval';
import { useFundingChoice } from './useFundingChoice';
import { usePotEmptyNotice } from './usePotEmptyNotice';
import type { FundingChoiceState } from './useFundingChoice';
import { useAccessRecheck } from './useAccessRecheck';
import { useTopUpRecordedInvalidation } from './useTopUpRecordedInvalidation';
import { useMotorplanPayment } from './useMotorplanPayment';
import type { MotorplanPaymentState } from './useMotorplanPayment';
import type { FundingApproval } from './useFundingApproval';
import { isInvoicingPanelFocusable } from '../utils/invoicingPanelFocus';
import { useInvoicingView } from './useInvoicingView';
import { useTakingDealerLock, type TakingDealerLock } from './useTakingDealerLock';
import type { InvoicingViewState } from './useInvoicingView';
import { availableBalanceText, balanceUnreadableReason, excessGross, outcomeCard, stackedSummaryText } from '../utils/invoiceBalance';
import type { OutcomeCard } from '../utils/invoiceBalance';
import type { InvoiceSelection } from './useInvoiceSelection';
import type { InvoiceSubmission } from './useInvoiceSubmission';
import type {
    Agreement,
    AgreementCashPositionQuery,
    AgreementInvoicesQuery,
    InvoicingCandidatesQuery,
    SoldItemsQuery,
    TopUpPosting,
} from '../types/agreements.types';

export interface AgreementInvoicing {
    soldItemsQuery: SoldItemsQuery;
    flexible: boolean;
    invoiceSelection: InvoiceSelection;
    invoicesQuery: AgreementInvoicesQuery;
    cashPositionQuery: AgreementCashPositionQuery;
    /** Blocks Submit: the balance is loading or unreadable. Null once it is known. */
    balanceUnreadableReason: string | null;
    /** Ten-thousandths; 0 with nothing selected or while the balance is unknown. */
    excessGross: number;
    /** The over-balance section shows and the funding choice is kept only while this holds. */
    overBalance: boolean;
    fundingApproval: FundingApproval;
    fundingChoice: FundingChoiceState;
    /** The pot has cash to claim, so Invoice as pot total is offered beside the other two choices. */
    potTotalAvailable: boolean;
    /** The formatted balance, shown whatever the permission; null while loading or unreadable. */
    availableBalance: string | null;
    /** The outcome card in This invoice; an unavailable balance blocks Submit and renders as a warning. */
    outcome: OutcomeCard | null;
    stackedSummary: string | null;
    candidatesQuery: InvoicingCandidatesQuery;
    dealerNames: Map<string, string>;
    submission: InvoiceSubmission;
    /** Null while loading, on error, or when nothing is recorded: the form then checks nothing. */
    recordedMileage: number | null;
    canBrowseInvoices: boolean;
    canAddInvoice: boolean;
    /** The candidates query never ran, so an empty list is unknown rather than none. */
    candidatesGated: boolean;
    panelFocusable: boolean;
    view: InvoicingViewState;
    /** Lives here, not in the tab body, so it survives the tab going inactive while the top-up is held. */
    dealerLock: TakingDealerLock;
    topUpPosting: TopUpPosting;
    /** Survives a remount of the tab while the watch is posting or timed out. */
    recordedTopUpAmount: string | null;
    onTopUpRecorded: (recordedAmount: string) => void;
    onTopUpRefused: () => void;
    motorplan: MotorplanPaymentState;
    reversal: InvoiceReversal;
}

/** dealers-service rejects more than 50 ids per `/dealers/by-ids` request; ids beyond
 *  the cap keep the raw-id fallback. */
const DEALER_LOOKUP_ID_LIMIT = 50;

const neverReversible = () => false;

/** Composes the sold-items, invoices, candidates and submission logic for the
 *  Invoicing tab; nothing fires until `active` is true. */
export function useAgreementInvoicing(
    agreementId: string | undefined,
    agreement: Agreement | undefined,
    active: boolean,
): AgreementInvoicing {
    const { hasPermission } = usePermissions();
    const canBrowseInvoices = hasPermission({ resource: 'invoice', action: 'browse' });
    const canAddInvoice = hasPermission({ resource: 'invoice', action: 'add' });
    const canBrowseDealers = hasPermission({ resource: 'dealer', action: 'browse' });
    const canLoadIntoArrears = hasPermission({ resource: 'invoice', action: 'load_into_arrears' });
    const canClaimPotTotal = hasPermission({ resource: 'invoice', action: 'pot_total' });
    const canReadVehicle = hasPermission({ resource: 'vehicle', action: 'read' });
    const canCheckSecurity = hasPermission({ resource: 'security', action: 'check' });
    const canInvoiceFlexibly = hasPermission({ resource: 'invoice', action: 'flexible' });

    const soldItemsRawQuery = useAgreementSoldItemsQuery(agreementId, active);
    const invoicesQuery = useAgreementInvoicesQuery(agreementId, active && canBrowseInvoices);
    const reconciledLines = useReconciledSoldLines(soldItemsRawQuery.data, invoicesQuery.data);

    const soldItemsQuery: SoldItemsQuery = {
        data: reconciledLines,
        isLoading: soldItemsRawQuery.isLoading,
        isError: soldItemsRawQuery.isError,
        refetch: soldItemsRawQuery.refetch,
    };

    const invoiceSelection = useInvoiceSelection(agreementId, reconciledLines, canInvoiceFlexibly);

    const candidatesEnabled = active && !!agreement?.dealerId && canAddInvoice && canCheckSecurity;
    const candidatesQuery = useInvoicingCandidatesQuery(
        agreement?.dealerId,
        agreement?.productId ?? null,
        candidatesEnabled,
    );

    const watch = useCashPositionWatch(agreementId, active);
    const candidateDealerIds = useMemo(() => (candidatesQuery.data ?? []).map((candidate) => candidate.id), [candidatesQuery.data]);
    const reversal = useInvoiceReversal({
        agreementId,
        vehicleId: agreement?.vehicleId,
        agreementActive: agreement?.status === 'active',
        candidateDealerIds,
        watch,
    });
    const cashPositionRawQuery = watch.query;
    const watching = watch.posting === 'posting';
    const holdingAfterTopUp = watch.posting !== 'idle' && cashPositionRawQuery.data !== undefined;
    // Outside a watch a refetch (notably a submission's) supersedes the held figure;
    // during one, Submit is already blocked so the held figure is never acted on.
    const balanceStatus = {
        isLoading: cashPositionRawQuery.isLoading || reversal.settling || (cashPositionRawQuery.isFetching && !watching),
        isError: cashPositionRawQuery.isError && !holdingAfterTopUp,
        cashPosition: cashPositionRawQuery.data?.cashPosition,
    };
    const cashPositionQuery: AgreementCashPositionQuery = {
        data: cashPositionRawQuery.data,
        isLoading: cashPositionRawQuery.isLoading,
        isError: cashPositionRawQuery.isError,
    };
    const hasSelection = invoiceSelection.selection.length > 0;
    const excess = excessGross({ ...balanceStatus, selectionGross: invoiceSelection.totals.gross, hasSelection });
    const availableBalance = availableBalanceText(balanceStatus);
    const potTotalAvailable = balanceStatus.cashPosition !== undefined && toScaled(balanceStatus.cashPosition) > 0;
    const potEmpty = usePotEmptyNotice(agreementId);
    const fundingChoice = potEmpty.clearingChoice(
        useFundingChoice(excess > 0, potTotalAvailable, watch.posting !== 'idle'),
    );
    const purpose = fundingChoice.choice === 'pot_total' ? 'pot_total' : 'arrears';
    const outcome = outcomeCard(balanceStatus, invoiceSelection.totals.gross, hasSelection, fundingChoice.choice);
    const stackedSummary = hasSelection
        ? stackedSummaryText(
              invoiceSelection.summaryLines.length,
              invoiceSelection.totals.gross,
              balanceStatus,
              fundingChoice.choice,
          )
        : null;
    const fundingApproval = useFundingApproval({
        agreementId,
        purpose,
        lines: toInvoiceLines(invoiceSelection.selection),
        excessGross: excess,
        selectionGross: invoiceSelection.totals.gross,
        cashPosition: balanceStatus.cashPosition,
        canAuthorise: purpose === 'pot_total' ? canClaimPotTotal : canLoadIntoArrears,
        beginRejection: potEmpty.begin,
    });

    const candidateNames = useMemo(() => buildDealerNameMap(candidatesQuery.data), [candidatesQuery.data]);
    const remainingDealerIds = useMemo(
        () =>
            Array.from(new Set((invoicesQuery.data ?? []).map((invoice) => invoice.invoicingDealerId)))
                .filter((id) => !candidateNames.has(id))
                .slice(0, DEALER_LOOKUP_ID_LIMIT),
        [invoicesQuery.data, candidateNames],
    );
    // Candidates cover most invoicing dealer ids, so the fallback waits for them;
    // a disabled or errored candidates query settles immediately.
    const candidatesSettled = !candidatesEnabled || candidatesQuery.isSuccess || candidatesQuery.isError;
    const remainingDealersQuery = useDealersByIds(
        remainingDealerIds,
        active && canBrowseDealers && candidatesSettled,
    );
    const dealerNames = useMemo(() => {
        const map = new Map(candidateNames);
        for (const [id, name] of buildDealerNameMap(remainingDealersQuery.data)) {
            if (!map.has(id)) map.set(id, name);
        }
        return map;
    }, [candidateNames, remainingDealersQuery.data]);

    const onTopUpRecorded = useTopUpRecordedInvalidation(agreementId, watch.startWatch);
    const onTopUpRefused = useAccessRecheck(agreementId);
    const motorplan = useMotorplanPayment(agreementId, agreement, excess, onTopUpRefused);
    const submission = potEmpty.withSubmission(
        useInvoiceSubmission(agreementId ?? '', invoiceSelection, fundingApproval.override, fundingApproval.resetCodeCheck),
    );

    // Refetched on every mount or re-enable: a figure cached before the last submission is stale.
    const mileageQuery = useVehicleMileage(
        active && canAddInvoice && canReadVehicle ? agreement?.vehicleId || undefined : undefined,
        { staleTime: 0 },
    );
    const recordedMileage = mileageQuery.isError ? null : (mileageQuery.data?.mileage ?? null);

    const invoiceCount = invoicesQuery.data?.length ?? 0;
    const dealerLock = useTakingDealerLock(agreementId);
    const view = useInvoicingView({
        agreementId,
        active,
        canBrowseInvoices,
        canAddInvoice,
        invoicesLoading: invoicesQuery.isLoading,
        invoicesError: invoicesQuery.isError,
        invoiceCount,
        dismissNotice: submission.dismissNotice,
    });
    const panelFocusable = isInvoicingPanelFocusable({
        view: view.view,
        canBrowseInvoices,
        raise: {
            isLoading: soldItemsQuery.isLoading,
            isError: soldItemsQuery.isError,
            lines: soldItemsQuery.data,
            selectionCount: invoiceSelection.selection.length,
        },
        previouslyInvoiced: {
            isLoading: invoicesQuery.isLoading,
            isError: invoicesQuery.isError,
            invoiceCount,
            canAddInvoice,
            soldLines: {
                isLoading: soldItemsQuery.isLoading,
                isError: soldItemsQuery.isError,
                lines: soldItemsQuery.data,
            },
        },
    });

    return {
        soldItemsQuery,
        flexible: canInvoiceFlexibly,
        invoiceSelection,
        invoicesQuery,
        cashPositionQuery,
        balanceUnreadableReason: balanceUnreadableReason(balanceStatus),
        excessGross: excess,
        overBalance: excess > 0,
        fundingApproval,
        fundingChoice,
        potTotalAvailable,
        availableBalance,
        outcome,
        stackedSummary,
        candidatesQuery,
        dealerNames,
        submission,
        recordedMileage,
        canBrowseInvoices,
        canAddInvoice,
        candidatesGated: !candidatesEnabled,
        panelFocusable,
        view: {
            ...view,
            showPreviouslyInvoiced: () => {
                dealerLock.release();
                view.showPreviouslyInvoiced();
            },
        },
        dealerLock,
        topUpPosting: watch.posting,
        recordedTopUpAmount: watch.recordedAmount,
        onTopUpRecorded,
        onTopUpRefused,
        motorplan,
        // A reversal watch started mid-claim can stop on the claim's debit and miss the restoration.
        reversal: submission.isPending || submission.settling ? { ...reversal, canReverse: neverReversible } : reversal,
    };
}
